import { useState, useCallback } from 'react';

const KEYS = {
  NICKNAME: 'draw-lab-nickname',
  ROOM_HISTORY: 'draw-lab-roomHistory',
};

const MAX_ROOM_HISTORY = 20;
const ROOM_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadRoomHistory() {
  try {
    const stored = localStorage.getItem(KEYS.ROOM_HISTORY);
    if (!stored) return [];
    const rooms = JSON.parse(stored);
    // Filter out expired rooms
    const now = Date.now();
    return rooms.filter(r => now - r.lastVisited < ROOM_EXPIRY_MS);
  } catch {
    return [];
  }
}

function saveRoomHistory(rooms) {
  localStorage.setItem(KEYS.ROOM_HISTORY, JSON.stringify(rooms));
}

/**
 * Local session hook.
 * The userId is now the Firebase Auth UID, passed in from the
 * anonymous auth initialisation in main.jsx.
 */
export function useLocalSession(authUid) {
  const [userId] = useState(authUid);
  const [nickname, setNicknameState] = useState(
    () => localStorage.getItem(KEYS.NICKNAME) || ''
  );
  const [roomHistory, setRoomHistoryState] = useState(loadRoomHistory);

  const setNickname = useCallback((name) => {
    setNicknameState(name);
    localStorage.setItem(KEYS.NICKNAME, name);
  }, []);

  // Add or update a room in history
  const addRoomToHistory = useCallback((roomData) => {
    setRoomHistoryState((prev) => {
      const { code, name } = roomData;
      // Remove existing entry for this code
      const filtered = prev.filter(r => r.code !== code);
      // Add to front
      const updated = [
        { code, name, lastVisited: Date.now() },
        ...filtered,
      ].slice(0, MAX_ROOM_HISTORY);
      saveRoomHistory(updated);
      return updated;
    });
  }, []);

  // Remove a room from history
  const removeRoomFromHistory = useCallback((code) => {
    setRoomHistoryState((prev) => {
      const updated = prev.filter(r => r.code !== code);
      saveRoomHistory(updated);
      return updated;
    });
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(KEYS.NICKNAME);
    localStorage.removeItem(KEYS.ROOM_HISTORY);
    setNicknameState('');
    setRoomHistoryState([]);
  }, []);

  // Most recent room (for backwards compatibility)
  const lastRoom = roomHistory.length > 0 ? roomHistory[0] : null;

  return {
    userId,
    nickname,
    setNickname,
    lastRoom,
    roomHistory,
    addRoomToHistory,
    removeRoomFromHistory,
    clearSession,
  };
}
